import { useEffect, useSyncExternalStore } from 'react';

/**
 * Home-screen install, without a service worker (Chrome dropped that
 * requirement in 2022 — a valid manifest is enough).
 *
 * Chromium fires `beforeinstallprompt` once per page load when the app is
 * installable. We stash the event at module scope instead of letting the
 * browser use it, which is what lets a MENU ITEM re-open the native prompt
 * even after the teacher dismissed Chrome's own ambient suggestion (Chrome
 * then goes quiet for ~90 days — the stashed event does not).
 *
 * iOS Safari has no such event and no API at all: `canPrompt` stays false
 * there forever, and the caller shows manual instructions instead (detected
 * via `isIOS`). Inside an installed app (`display-mode: standalone`) the
 * item should not render at all.
 *
 * Module-scope store + `useSyncExternalStore` rather than component state:
 * the event fires ONCE, often before the menu ever mounts — state created on
 * mount would miss it.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    // iOS's pre-standard flag, still what an added-to-home-screen page reports.
    (navigator as unknown as { standalone?: boolean }).standalone === true);

export const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ masquerades as macOS; the touch check unmasks it.
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1));

export function useInstallPrompt() {
  const canPrompt = useSyncExternalStore(
    subscribe,
    () => deferred !== null,
    () => false,
  );
  // Belt-and-braces: some Chromium builds fire the event before this module
  // is evaluated when the bundle is code-split. Nothing to do about a missed
  // event beyond accepting `canPrompt: false` — this effect only keeps the
  // subscription warm for late fires.
  useEffect(() => undefined, []);

  const promptInstall = async () => {
    if (!deferred) return;
    const ev = deferred;
    // One shot per event: Chrome throws on a second .prompt() of the same
    // event, so clear first and let a future beforeinstallprompt re-arm.
    deferred = null;
    notify();
    await ev.prompt();
  };

  return { canPrompt, promptInstall, isIOS: isIOS(), standalone: isStandalone() };
}
